import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Job, JobImportBatch } from "@career-ops-amplify/shared";
import {
  createJobImportBatch,
  getJobImportBatch,
  getJobImportBatchArtifactUrl,
  getJob,
  listJobs,
  dismissJobsNoGoBulk,
  parseJobUrlBatch,
} from "@/api/jobs";
import { cn } from "@/cn";
import { isObviousNonMatchForCto } from "@/lib/job-title-fit";
import { ConfirmDialog } from "@/components/confirm-dialog";
import {
  JOB_CSV_FIELDS,
  JOB_CSV_SAMPLE_HEADERS,
  applyJobMapping,
  detectJobCsvInput,
  downloadJobCsvSample,
  rowImportReady,
  saveMapping,
  type ColumnMapping,
  type JobCsvField,
  type MappedJobRow,
} from "@/lib/job-csv-mapping";

const BATCH_CHUNK = 25;
const TERMINAL = new Set([
  "completed",
  "completed_with_errors",
  "failed",
]);

type Phase = "input" | "mapping" | "review" | "running" | "done";

type BatchSummaryArtifact = {
  jobIds?: string[];
};

async function pollBatchUntilDone(batchId: string): Promise<JobImportBatch> {
  for (let i = 0; i < 120; i++) {
    const { batch } = await getJobImportBatch(batchId);
    if (TERMINAL.has(batch.status)) return batch;
    await new Promise((r) => setTimeout(r, 1000));
  }
  const { batch } = await getJobImportBatch(batchId);
  return batch;
}

/** Prefer summary.json jobIds; fall back to listJobs filtered by batchId. */
async function loadObviousNonMatchesForBatch(
  batch: JobImportBatch,
): Promise<Job[]> {
  let candidates: Job[] = [];

  if (batch.summaryKey) {
    try {
      const { url } = await getJobImportBatchArtifactUrl(
        batch.batchId,
        "summary",
      );
      const res = await fetch(url);
      if (res.ok) {
        const summary = (await res.json()) as BatchSummaryArtifact;
        const ids = Array.isArray(summary.jobIds)
          ? summary.jobIds.filter(Boolean)
          : [];
        if (ids.length > 0) {
          const settled = await Promise.allSettled(ids.map((id) => getJob(id)));
          candidates = settled
            .filter(
              (r): r is PromiseFulfilledResult<Job> => r.status === "fulfilled",
            )
            .map((r) => r.value);
        }
      }
    } catch {
      // fall through to listJobs
    }
  }

  if (candidates.length === 0) {
    const { jobs } = await listJobs();
    candidates = jobs.filter((j) => j.batchId === batch.batchId);
  }

  return candidates.filter(
    (j) =>
      !j.pipelineId &&
      j.status !== "no_go" &&
      isObviousNonMatchForCto(j.role),
  );
}

export function JobsAddBatch() {
  const qc = useQueryClient();
  const [phase, setPhase] = useState<Phase>("input");
  const [raw, setRaw] = useState("");
  const [fileName, setFileName] = useState("original.csv");
  const [headers, setHeaders] = useState<string[]>([]);
  const [csvRows, setCsvRows] = useState<string[][]>([]);
  const [mapping, setMapping] = useState<ColumnMapping>({});
  const [rows, setRows] = useState<MappedJobRow[]>([]);
  const [error, setError] = useState("");
  const [batch, setBatch] = useState<JobImportBatch | null>(null);
  const [enriching, setEnriching] = useState(false);
  const [enrichProgress, setEnrichProgress] = useState("");
  const [runProgress, setRunProgress] = useState("");
  const [nonMatchIds, setNonMatchIds] = useState<string[]>([]);
  const [nonMatchPreview, setNonMatchPreview] = useState<
    Array<{ id: string; company: string; role: string }>
  >([]);
  const [confirmNonMatchOpen, setConfirmNonMatchOpen] = useState(false);
  const [nonMatchBusy, setNonMatchBusy] = useState(false);
  const [nonMatchResult, setNonMatchResult] = useState<string | null>(null);

  const selectedCount = useMemo(
    () => rows.filter((r) => r.selected && rowImportReady(r)).length,
    [rows],
  );

  const handleFile = useCallback(async (file: File | null) => {
    if (!file) return;
    const text = await file.text();
    setRaw(text);
    setFileName(file.name || "original.csv");
    setError("");
  }, []);

  const handleDetect = () => {
    setError("");
    setBatch(null);
    const detected = detectJobCsvInput(raw);
    if (detected.rows.length === 0) {
      setError("No rows found. Paste a CSV with headers or a list of job URLs.");
      setRows([]);
      setPhase("input");
      return;
    }
    setHeaders(detected.headers);
    setCsvRows(detected.rows);
    setMapping(detected.mapping);
    if (detected.urlOnly || !detected.hasHeader) {
      const mapped = applyJobMapping(detected.headers, detected.rows, detected.mapping);
      setRows(mapped);
      setPhase("review");
      return;
    }
    setPhase("mapping");
  };

  const handleApplyMapping = () => {
    if (![...Object.values(mapping)].includes("url")) {
      setError("Map at least one column to Job URL.");
      return;
    }
    saveMapping(headers, mapping);
    const mapped = applyJobMapping(headers, csvRows, mapping);
    if (mapped.length === 0) {
      setError("No valid job URLs after mapping. Check the URL column.");
      return;
    }
    setError("");
    setRows(mapped);
    setPhase("review");
  };

  const updateRow = (id: string, patch: Partial<MappedJobRow>) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        const next = { ...r, ...patch };
        if (patch.company !== undefined || patch.role !== undefined) {
          next.selected = rowImportReady(next) ? next.selected || true : false;
        }
        return next;
      }),
    );
  };

  /** Optional preview enrich — commit path enriches server-side. */
  const handleEnrich = async () => {
    setEnriching(true);
    setError("");
    try {
      const targets = rows.filter((r) => r.url);
      setRows((prev) =>
        prev.map((r) =>
          targets.some((t) => t.id === r.id)
            ? { ...r, enrichStatus: "pending" as const }
            : r,
        ),
      );

      for (let offset = 0; offset < targets.length; offset += BATCH_CHUNK) {
        const chunk = targets.slice(offset, offset + BATCH_CHUNK);
        setEnrichProgress(
          `Fetching ${offset + 1}–${Math.min(offset + BATCH_CHUNK, targets.length)} of ${targets.length}…`,
        );
        const { results } = await parseJobUrlBatch(
          chunk.map((r) => r.url),
          "offline",
        );

        setRows((prev) =>
          prev.map((r) => {
            const idx = chunk.findIndex((c) => c.id === r.id);
            if (idx < 0) return r;
            const result = results[idx];
            if (!result) {
              return { ...r, enrichStatus: "failed", hint: "No result" };
            }
            const fields = result.fields ?? {};
            const company = r.company.trim() || fields.company || "";
            const role = r.role.trim() || fields.role || "";
            const location = r.location?.trim() || fields.location || undefined;
            const compensation =
              r.compensation?.trim() || fields.compensation || undefined;
            const jobDescription =
              r.jobDescription?.trim() || fields.jobDescription || undefined;
            const ready = Boolean(company && role);
            const status: MappedJobRow["enrichStatus"] = result.success
              ? result.tier === "manual"
                ? "manual"
                : "ok"
              : ready
                ? "manual"
                : "failed";
            return {
              ...r,
              company,
              role,
              location,
              compensation,
              jobDescription,
              resolvedUrl: result.resolvedUrl,
              tier: result.tier,
              provider: result.provider,
              hint: result.hint,
              enrichStatus: status,
              selected: ready,
            };
          }),
        );
      }
      setEnrichProgress("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Enrichment failed");
      setRows((prev) =>
        prev.map((r) =>
          r.enrichStatus === "pending" ? { ...r, enrichStatus: "failed" } : r,
        ),
      );
    } finally {
      setEnriching(false);
      setEnrichProgress("");
    }
  };

  const importMutation = useMutation({
    mutationFn: async () => {
      if (![...Object.values(mapping)].includes("url") && headers.length === 0) {
        // URL-only path uses mapping {0: url}
      }
      const columns: Record<string, string> = {};
      for (const [k, v] of Object.entries(mapping)) {
        columns[String(k)] = v;
      }
      if (!Object.values(columns).includes("url")) {
        columns["0"] = "url";
      }

      setPhase("running");
      setRunProgress("Landing CSV and processing batch…");

      const { batch: created } = await createJobImportBatch({
        csvText: raw,
        fileName,
        contentType: "text/csv",
        mapping: {
          headers: headers.length ? headers : ["url"],
          columns,
        },
      });

      // v1 processes inline; poll in case status is still in-flight
      if (!TERMINAL.has(created.status)) {
        setRunProgress("Processing…");
        return pollBatchUntilDone(created.batchId);
      }
      return created;
    },
    onSuccess: async (result) => {
      qc.invalidateQueries({ queryKey: ["jobs"] });
      setBatch(result);
      setRunProgress("");
      setPhase("done");
      setNonMatchIds([]);
      setNonMatchPreview([]);
      setNonMatchResult(null);
      if (result.status === "failed") {
        setError(result.errorMessage || "Batch processing failed");
        return;
      }
      try {
        const nonMatches = await loadObviousNonMatchesForBatch(result);
        setNonMatchIds(nonMatches.map((j) => j.id));
        setNonMatchPreview(
          nonMatches.slice(0, 8).map((j) => ({
            id: j.id,
            company: j.company,
            role: j.role,
          })),
        );
      } catch {
        // Non-blocking: import succeeded even if triage preview fails
      }
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Batch import failed");
      setRunProgress("");
      setPhase("review");
    },
  });

  const downloadArtifact = async (kind: "original" | "rejects" | "summary") => {
    if (!batch) return;
    try {
      const { url } = await getJobImportBatchArtifactUrl(batch.batchId, kind);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Download failed");
    }
  };

  const reset = () => {
    setPhase("input");
    setRaw("");
    setFileName("original.csv");
    setHeaders([]);
    setCsvRows([]);
    setMapping({});
    setRows([]);
    setError("");
    setBatch(null);
    setRunProgress("");
    setNonMatchIds([]);
    setNonMatchPreview([]);
    setNonMatchResult(null);
  };

  const runBulkNoGoNonMatches = async () => {
    if (nonMatchIds.length === 0) return;
    setNonMatchBusy(true);
    try {
      const result = await dismissJobsNoGoBulk(
        nonMatchIds,
        "Obvious non-match (title triage)",
      );
      qc.invalidateQueries({ queryKey: ["jobs"] });
      setNonMatchResult(
        `Marked ${result.counts.ok} no-go` +
          (result.counts.skippedPiped
            ? ` · skipped ${result.counts.skippedPiped} piped`
            : ""),
      );
      setNonMatchIds([]);
      setNonMatchPreview([]);
      setConfirmNonMatchOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Bulk no-go failed");
      setConfirmNonMatchOpen(false);
    } finally {
      setNonMatchBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Upload or paste a CSV, map columns to job fields, optionally preview URL details, then run a server batch (lands in S3, enriches, imports, writes rejects).
      </p>

      {phase === "input" && (
        <div className="space-y-4">
          <div className="space-y-4 rounded-lg border border-border bg-card p-6">
            <div>
              <label className="mb-2 block text-xs font-medium text-foreground">
                CSV file
              </label>
              <input
                type="file"
                accept=".csv,text/csv,text/plain"
                onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
                className="block w-full text-sm text-muted-foreground file:mr-3 file:rounded-md file:border file:border-border file:bg-bg file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-foreground"
              />
            </div>
            <div>
              <label className="mb-2 block text-xs font-medium text-foreground">
                Or paste CSV / job URLs
              </label>
              <textarea
                value={raw}
                onChange={(e) => setRaw(e.target.value)}
                rows={10}
                placeholder={"url,company,role,location\nhttps://boards.greenhouse.io/…,Acme,Staff Engineer,Remote\n\n# or one URL per line:\nhttps://www.linkedin.com/jobs/view/…"}
                className="w-full rounded-md border border-border bg-bg px-3 py-2 font-mono text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand"
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <button
              type="button"
              onClick={handleDetect}
              disabled={!raw.trim()}
              className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground disabled:opacity-50"
            >
              Continue
            </button>
          </div>

          <details className="rounded-lg border border-border bg-card p-5 open:pb-5">
            <summary className="cursor-pointer text-sm font-semibold text-foreground">
              Help — CSV format & sample file
            </summary>
            <div className="mt-4 space-y-4 text-sm text-muted-foreground">
              <p>
                UTF-8 CSV up to 5MB. Header row recommended. Only{" "}
                <span className="font-medium text-foreground">url</span> is required;
                company and role can be filled by URL enrich during the batch run.
              </p>
              <div>
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-foreground">
                  Columns
                </p>
                <ul className="space-y-1.5 text-xs">
                  {JOB_CSV_FIELDS.filter((f) => f.value !== "ignore").map((f) => (
                    <li key={f.value}>
                      <span className="font-mono text-foreground">
                        {f.value === "jobDescription" ? "description" : f.value}
                      </span>
                      {" — "}
                      {f.label}
                      {f.value === "url" ? " (required)" : " (optional)"}
                    </li>
                  ))}
                </ul>
              </div>
              <p className="text-xs">
                Accepted header aliases include{" "}
                <span className="font-mono">job_url</span>,{" "}
                <span className="font-mono">title</span>,{" "}
                <span className="font-mono">employer</span>,{" "}
                <span className="font-mono">salary</span>,{" "}
                <span className="font-mono">jd</span>. You can also paste one job URL
                per line with no headers.
              </p>
              <p className="font-mono text-[11px] leading-relaxed text-foreground/80">
                {JOB_CSV_SAMPLE_HEADERS.join(",")}
              </p>
              <button
                type="button"
                onClick={() => downloadJobCsvSample()}
                className="rounded-md border border-border bg-bg px-3 py-2 text-xs font-medium text-foreground hover:bg-muted/20"
              >
                Download sample CSV
              </button>
            </div>
          </details>
        </div>
      )}

      {phase === "mapping" && (
        <div className="space-y-4 rounded-lg border border-border bg-card p-6">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Map columns
          </h2>
          <p className="text-xs text-muted-foreground">
            Match each CSV column to a job field. URL is required.
          </p>
          <div className="space-y-2">
            {headers.map((header, index) => (
              <div
                key={`${header}-${index}`}
                className="grid grid-cols-[1fr_1fr] items-center gap-3"
              >
                <div className="truncate text-sm text-foreground" title={header}>
                  <span className="text-muted-foreground">Column:</span> {header || `(empty ${index})`}
                </div>
                <select
                  value={mapping[index] ?? "ignore"}
                  onChange={(e) =>
                    setMapping((prev) => ({
                      ...prev,
                      [index]: e.target.value as JobCsvField,
                    }))
                  }
                  className="rounded-md border border-border bg-bg px-2 py-1.5 text-sm text-foreground"
                >
                  {JOB_CSV_FIELDS.map((f) => (
                    <option key={f.value} value={f.value}>
                      {f.label}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
          {csvRows[0] && (
            <div className="rounded-md border border-border bg-muted/20 p-3 text-xs text-muted-foreground">
              <p className="mb-1 font-medium text-foreground">Sample row</p>
              {headers.map((h, i) => (
                <div key={i} className="truncate">
                  {h}: {csvRows[0][i] || "—"}
                </div>
              ))}
            </div>
          )}
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleApplyMapping}
              className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground"
            >
              Preview rows
            </button>
            <button
              type="button"
              onClick={() => setPhase("input")}
              className="rounded-md border border-border px-4 py-2 text-sm text-muted-foreground"
            >
              Back
            </button>
          </div>
        </div>
      )}

      {phase === "review" && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleEnrich}
              disabled={enriching || rows.length === 0}
              className="rounded-md border border-border px-3 py-2 text-xs font-medium text-foreground hover:bg-muted/20 disabled:opacity-50"
            >
              {enriching ? "Fetching…" : "Preview: fetch details from URLs"}
            </button>
            <button
              type="button"
              onClick={() =>
                setRows((prev) =>
                  prev.map((r) => ({
                    ...r,
                    selected: rowImportReady(r),
                  })),
                )
              }
              className="rounded-md border border-border px-3 py-2 text-xs font-medium text-muted-foreground"
            >
              Select ready
            </button>
            <button
              type="button"
              onClick={() => setPhase(headers.length && Object.keys(mapping).length ? "mapping" : "input")}
              className="rounded-md border border-border px-3 py-2 text-xs font-medium text-muted-foreground"
            >
              Back
            </button>
            {enrichProgress && (
              <span className="text-xs text-muted-foreground">{enrichProgress}</span>
            )}
          </div>

          <p className="text-xs text-muted-foreground">
            Run batch imports the full landed CSV (server enrich + import). Preview selection is for review only.
            {selectedCount > 0 ? ` ${selectedCount} row(s) look ready in preview.` : ""}
          </p>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-border bg-muted/20 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-2 py-2">In</th>
                  <th className="px-2 py-2">Company</th>
                  <th className="px-2 py-2">Role</th>
                  <th className="px-2 py-2">Location</th>
                  <th className="px-2 py-2">Status</th>
                  <th className="px-2 py-2">URL</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const ready = rowImportReady(r);
                  return (
                    <tr key={r.id} className="border-b border-border last:border-0 align-top">
                      <td className="px-2 py-2">
                        <input
                          type="checkbox"
                          checked={r.selected && ready}
                          disabled={!ready}
                          onChange={() =>
                            updateRow(r.id, { selected: !r.selected })
                          }
                        />
                      </td>
                      <td className="px-2 py-2">
                        <input
                          value={r.company}
                          onChange={(e) =>
                            updateRow(r.id, { company: e.target.value })
                          }
                          className="w-36 rounded border border-border bg-bg px-1.5 py-1 text-xs"
                          placeholder="Company"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <input
                          value={r.role}
                          onChange={(e) =>
                            updateRow(r.id, { role: e.target.value })
                          }
                          className="w-44 rounded border border-border bg-bg px-1.5 py-1 text-xs"
                          placeholder="Title"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <input
                          value={r.location ?? ""}
                          onChange={(e) =>
                            updateRow(r.id, { location: e.target.value })
                          }
                          className="w-32 rounded border border-border bg-bg px-1.5 py-1 text-xs"
                          placeholder="Location"
                        />
                      </td>
                      <td className="px-2 py-2 text-xs">
                        <span
                          className={cn(
                            "rounded px-1.5 py-0.5",
                            r.enrichStatus === "ok" && "bg-success/10 text-success",
                            r.enrichStatus === "manual" &&
                              "bg-amber-500/10 text-amber-600 dark:text-amber-400",
                            r.enrichStatus === "failed" && "bg-destructive/10 text-destructive",
                            (!r.enrichStatus || r.enrichStatus === "idle") &&
                              "text-muted-foreground",
                            r.enrichStatus === "pending" && "text-muted-foreground",
                          )}
                        >
                          {r.enrichStatus === "pending"
                            ? "…"
                            : r.tier || r.enrichStatus || (ready ? "ready" : "needs fields")}
                          {r.provider ? ` · ${r.provider}` : ""}
                        </span>
                        {!ready && (
                          <div className="mt-0.5 text-[10px] text-amber-600 dark:text-amber-400">
                            needs company + title
                          </div>
                        )}
                      </td>
                      <td className="max-w-[12rem] truncate px-2 py-2 text-xs text-muted-foreground">
                        <a
                          href={r.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-brand hover:underline"
                        >
                          {r.url}
                        </a>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <button
            type="button"
            onClick={() => importMutation.mutate()}
            disabled={importMutation.isPending || rows.length === 0}
            className="rounded-md bg-brand px-4 py-2.5 text-sm font-medium text-brand-foreground disabled:opacity-50"
          >
            {importMutation.isPending
              ? "Running batch…"
              : `Run batch import (${rows.length} row${rows.length === 1 ? "" : "s"})`}
          </button>
        </div>
      )}

      {phase === "running" && (
        <div className="rounded-lg border border-border bg-card p-8 text-center">
          <p className="text-sm text-muted-foreground">
            {runProgress || "Processing batch…"}
          </p>
        </div>
      )}

      {phase === "done" && batch && (
        <div
          className={cn(
            "rounded-lg border p-8 text-center",
            batch.status === "failed"
              ? "border-destructive/30 bg-destructive/5"
              : "border-success/30 bg-success/5",
          )}
        >
          <h2 className="mb-1 text-lg font-semibold text-foreground">
            {batch.status === "failed"
              ? "Batch failed"
              : batch.status === "completed_with_errors"
                ? "Batch completed with errors"
                : "Batch import complete"}
          </h2>
          <p className="mb-2 text-sm text-muted-foreground">
            Batch <span className="font-mono text-xs">{batch.batchId}</span>
          </p>
          <p className="mb-6 text-sm text-muted-foreground">
            Imported {batch.imported}
            {batch.skippedDupes > 0
              ? ` · skipped ${batch.skippedDupes} duplicate(s)`
              : ""}
            {batch.rejected > 0 ? ` · rejected ${batch.rejected}` : ""}
            {` · ${batch.totalRows} total row(s)`}
          </p>
          {error && <p className="mb-4 text-sm text-destructive">{error}</p>}
          {nonMatchResult && (
            <p className="mb-4 text-sm text-muted-foreground">{nonMatchResult}</p>
          )}
          {batch.status !== "failed" && (
            <div
              className="mb-6 rounded-lg border border-border bg-card/60 p-4 text-left"
              data-testid="import-post-triage"
            >
              <p className="text-sm font-medium text-foreground">
                Post-import triage
              </p>
              {batch.skippedDupes > 0 && (
                <p
                  className="mt-2 text-xs text-amber-700 dark:text-amber-400"
                  data-testid="import-skipped-dupes"
                >
                  Dedup: skipped {batch.skippedDupes} duplicate URL / company+role
                  row{batch.skippedDupes === 1 ? "" : "s"}.
                </p>
              )}
              {nonMatchIds.length > 0 ? (
                <>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Title heuristics flagged {nonMatchIds.length} sales / IC
                    engineer role
                    {nonMatchIds.length === 1 ? "" : "s"} (AE, RM, software
                    engineer, …). Preview:
                  </p>
                  <ul className="mt-2 max-h-32 space-y-1 overflow-y-auto text-xs text-muted-foreground">
                    {nonMatchPreview.map((j) => (
                      <li key={j.id}>
                        {j.company} — {j.role}
                      </li>
                    ))}
                    {nonMatchIds.length > nonMatchPreview.length ? (
                      <li>
                        …and {nonMatchIds.length - nonMatchPreview.length} more
                      </li>
                    ) : null}
                  </ul>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      data-testid="import-bulk-nogo-nonmatches"
                      onClick={() => setConfirmNonMatchOpen(true)}
                      className="rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-brand-foreground"
                    >
                      No-go obvious non-matches
                    </button>
                    <Link
                      to="/jobs?titleFit=sales"
                      className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground"
                    >
                      Review sales titles in Inbox
                    </Link>
                  </div>
                </>
              ) : (
                <>
                  <p className="mt-2 text-xs text-muted-foreground">
                    No obvious sales/IC non-matches in this batch.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Link
                      to="/jobs"
                      className="rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-brand-foreground"
                      data-testid="import-open-inbox"
                    >
                      Open Inbox
                    </Link>
                    <Link
                      to="/jobs?titleFit=cto_like"
                      className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground"
                      data-testid="import-filter-cto"
                    >
                      Filter CTO-shaped
                    </Link>
                  </div>
                </>
              )}
            </div>
          )}
          <div className="mb-6 flex flex-wrap justify-center gap-2">
            {batch.exceptionKey && (
              <button
                type="button"
                onClick={() => downloadArtifact("rejects")}
                className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground"
              >
                Download rejects.csv
              </button>
            )}
            {batch.summaryKey && (
              <button
                type="button"
                onClick={() => downloadArtifact("summary")}
                className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground"
              >
                Download summary.json
              </button>
            )}
            <button
              type="button"
              onClick={() => downloadArtifact("original")}
              className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground"
            >
              Download original.csv
            </button>
          </div>
          <div className="flex justify-center gap-3">
            <Link
              to="/jobs"
              className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground"
            >
              View Jobs
            </Link>
            <button
              type="button"
              onClick={reset}
              className="rounded-md border border-border px-4 py-2 text-sm text-muted-foreground"
            >
              Import another batch
            </button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmNonMatchOpen}
        title={`No-go ${nonMatchIds.length} obvious non-match${nonMatchIds.length === 1 ? "" : "es"}?`}
        description="Marks sales / IC engineer title fits from this batch as no-go. You can restore from the Jobs No-go tab."
        confirmLabel="Bulk No-go"
        confirmTone="danger"
        busy={nonMatchBusy}
        testId="import-nogo-nonmatches-confirm"
        onCancel={() => setConfirmNonMatchOpen(false)}
        onConfirm={() => void runBulkNoGoNonMatches()}
      />
    </div>
  );
}
